import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Users, ShieldCheck, Building2, Briefcase, Mail, MailCheck, CheckCircle2, XCircle, Power, Trash2, RefreshCw, LogIn, Clock, ShieldPlus, KeyRound } from "lucide-react";
import api, { setToken } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";

export default function AreaManagers() {
  const { t } = useT();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/area-managers");
      setItems(res.data || []);
      setError("");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : t("load_failed"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggleActive = async (am) => {
    if (!window.confirm(am.is_active ? t("confirm_deactivate") : t("confirm_activate"))) return;
    await api.patch(`/area-managers/${am.id}`, { is_active: !am.is_active });
    load();
  };

  const approve = async (am) => {
    if (!window.confirm(t("confirm_approve").replace("{name}", am.full_name || am.username))) return;
    try {
      await api.patch(`/area-managers/${am.id}`, { is_active: true });
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      window.alert(typeof d === "string" ? d : t("save_failed"));
    }
  };

  const verifyEmail = async (am) => {
    if (!window.confirm(t("confirm_verify_email").replace("{email}", am.email || "—"))) return;
    try {
      await api.patch(`/area-managers/${am.id}`, { email_verified: true });
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      window.alert(typeof d === "string" ? d : t("save_failed"));
    }
  };

  const remove = async (am) => {
    if (!window.confirm(t("confirm_delete_area_manager_hard").replace("{name}", am.full_name || am.username))) return;
    try {
      await api.delete(`/area-managers/${am.id}`);
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      window.alert(typeof d === "string" ? d : t("save_failed"));
    }
  };

  const resetOtp = async (am) => {
    if (!window.confirm(t("ss_otp_confirm", { name: am.full_name || am.username }))) return;
    try {
      const res = await api.post(`/users/${am.id}/reset-otp`);
      const chs = (res.data.channels || []).map((c) => t(`ss_ch_${c}`)).join("، ");
      window.alert(t("ss_otp_sent", { channels: chs }));
      if (res.data.user_telegram_contact) {
        window.prompt(t("ss_otp_tg_contact"), res.data.user_telegram_contact);
      }
    } catch (err) {
      const d = err.response?.data?.detail;
      window.alert(typeof d === "string" ? d : t("save_failed"));
    }
  };

  const impersonate = async (am) => {
    if (!window.confirm(t("confirm_impersonate").replace("{name}", am.full_name || am.username))) return;
    try {
      const res = await api.post(`/area-managers/${am.id}/impersonate`);
      setToken(res.data.token);
      await refresh();
      navigate("/");
    } catch (err) {
      const d = err.response?.data?.detail;
      window.alert(typeof d === "string" ? d : t("load_failed"));
    }
  };

  return (
    <div className="space-y-5" data-testid="area-managers-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-teal-900 flex items-center gap-2">
            <Users size={20} /> {t("nav_area_managers")}
          </h2>
          <p className="text-sm text-gray-500">{t("area_managers_subtitle")}</p>
        </div>
        <button onClick={load} className="btn btn-secondary" data-testid="reload-area-managers">
          <RefreshCw size={14} /> {t("refresh")}
        </button>
      </div>

      {error && (
        <div className="status-expired pill px-3 py-2" data-testid="area-managers-error">{error}</div>
      )}

      {loading ? (
        <div className="text-gray-400 text-sm text-center py-12">{t("loading")}…</div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-2xl card-shadow p-10 text-center text-gray-500" data-testid="area-managers-empty">
          {t("no_area_managers")}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((am) => (
            <div key={am.id} className="bg-white rounded-2xl card-shadow p-5" data-testid={`area-manager-card-${am.id}`}>
              <div className="flex items-start justify-between gap-2 mb-3">
                <div className="min-w-0">
                  <h3 className="font-bold text-teal-900 truncate">{am.full_name || am.username}</h3>
                  <p className="text-xs text-gray-500" style={{ direction: "ltr", textAlign: "start" }}>@{am.username}</p>
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  {am.pending_approval ? (
                    <span className="pill status-warning" data-testid={`am-pending-${am.id}`}><Clock size={12} /> {t("pending_approval_badge")}</span>
                  ) : am.is_active ? (
                    <span className="pill status-valid"><CheckCircle2 size={12} /> {t("active")}</span>
                  ) : (
                    <span className="pill status-expired"><XCircle size={12} /> {t("inactive")}</span>
                  )}
                  {am.email_verified ? (
                    <span className="text-[10px] text-emerald-700">✓ {t("email_verified")}</span>
                  ) : (
                    <button
                      onClick={() => verifyEmail(am)}
                      className="text-[10px] text-amber-700 hover:text-emerald-700 hover:underline cursor-pointer"
                      data-testid={`am-verify-email-${am.id}`}
                      title={t("verify_email_manually")}
                    >
                      ⌛ {t("email_unverified")}
                    </button>
                  )}
                </div>
              </div>

              {am.email && (
                <div className="flex items-center gap-1.5 text-xs text-gray-600 mb-3" style={{ direction: "ltr" }}>
                  <Mail size={12} /> {am.email}
                </div>
              )}

              <div className="grid grid-cols-3 gap-2 text-center text-xs mb-4">
                <div className="bg-teal-50 rounded-lg py-2" data-testid={`am-branches-${am.id}`}>
                  <Building2 size={14} className="inline text-teal-700 mb-0.5" />
                  <div className="font-bold text-teal-900">{am.branches_count}</div>
                  <div className="text-[10px] text-gray-500">{t("branches")}</div>
                </div>
                <div className="bg-amber-50 rounded-lg py-2" data-testid={`am-admins-${am.id}`}>
                  <ShieldCheck size={14} className="inline text-amber-700 mb-0.5" />
                  <div className="font-bold text-amber-900">{am.admins_count}</div>
                  <div className="text-[10px] text-gray-500">{t("supervisors")}</div>
                </div>
                <div className="bg-rose-50 rounded-lg py-2" data-testid={`am-employees-${am.id}`}>
                  <Briefcase size={14} className="inline text-rose-700 mb-0.5" />
                  <div className="font-bold text-rose-900">{am.employees_count}</div>
                  <div className="text-[10px] text-gray-500">{t("employees")}</div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {am.pending_approval ? (
                  <button
                    onClick={() => approve(am)}
                    className="btn btn-primary flex-1 justify-center bg-emerald-600 hover:bg-emerald-700"
                    data-testid={`am-approve-${am.id}`}
                    title={t("approve_account")}
                  >
                    <ShieldPlus size={14} /> {t("approve_account")}
                  </button>
                ) : (
                  <button
                    onClick={() => impersonate(am)}
                    disabled={!am.is_active}
                    className="btn btn-primary flex-1 justify-center"
                    data-testid={`am-impersonate-${am.id}`}
                    title={t("login_as_manager")}
                  >
                    <LogIn size={13} /> {t("login_as")}
                  </button>
                )}
                <button
                  onClick={() => resetOtp(am)}
                  className="btn btn-ghost text-teal-700 hover:bg-teal-50"
                  data-testid={`am-reset-otp-${am.id}`}
                  title={t("ss_reset_otp")}
                >
                  <KeyRound size={13} />
                </button>
                <button
                  onClick={() => toggleActive(am)}
                  className={`btn btn-ghost ${am.is_active ? "text-amber-700" : "text-emerald-700"}`}
                  data-testid={`am-toggle-${am.id}`}
                  title={am.is_active ? t("deactivate") : t("activate")}
                >
                  <Power size={13} />
                </button>
                <button
                  onClick={() => remove(am)}
                  className="btn btn-ghost text-rose-600 hover:bg-rose-50"
                  data-testid={`am-delete-${am.id}`}
                  title={t("delete")}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
